import { zodResolver } from '@hookform/resolvers/zod'
import NoImagePlaceholder from 'assets/no-image-placeholder.png'
import Button from 'components/Button'
import FormRow from 'components/Form/FormRow'
import InputField from 'components/Form/Inputs/InputField'
import SubmitButton from 'components/Form/SubmitButton'
import ModalBody from 'components/Modal/ModalBody'
import ModalFooter from 'components/Modal/ModalFooter'
import UrlModal from 'components/Modal/UrlModal'
import { menusQueryKey } from 'containers/menus/queries'
import { useGetRecipeCategoriesQuery } from 'containers/recipeCategories/queries'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { queryClient } from 'utils/queryClient'
import * as z from 'zod'
import { Combobox } from '../../../components/Form/Inputs/Combobox'
import { Label } from '../../../components/Form/Label'
import { recipesQueryKey, useEditRecipeMutation, useGetSingleRecipeQuery } from '../queries'

type Inputs = {
    name: string
    recipeCategoryName: string
    prepTime: string
    cookTime: string
    serves: string
}

const schema = z.object({
    name: z.string().min(1, 'Required'),
    recipeCategoryName: z.string(),
    prepTime: z.coerce.number(),
    cookTime: z.coerce.number(),
    serves: z.coerce.number()
})

function EditRecipeDetailsForm() {
    const navigate = useNavigate()
    const { recipeId } = useParams()

    const { data: getRecipeCategoriesData } = useGetRecipeCategoriesQuery()

    const { data: getSingleRecipeData } = useGetSingleRecipeQuery(recipeId || '')

    const { mutateAsync: editRecipe } = useEditRecipeMutation()

    const methods = useForm<Inputs>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            name: getSingleRecipeData?.name,
            recipeCategoryName: getSingleRecipeData?.recipe_category?.name || '',
            prepTime: getSingleRecipeData?.prep_time?.toString() || '15',
            cookTime: getSingleRecipeData?.cook_time?.toString() || '15',
            serves: getSingleRecipeData?.serves?.toString() || '1'
        }
    })

    const {
        handleSubmit,
        formState: { isValid, isSubmitting }
    } = methods

    const onSubmit: SubmitHandler<Inputs> = async ({ name, recipeCategoryName, prepTime, cookTime, serves }) => {
        await editRecipe(
            {
                recipeId: recipeId || '',
                attributes: {
                    name,
                    method: getSingleRecipeData?.method ?? [],
                    recipe_category: recipeCategoryName,
                    prep_time: Number(prepTime),
                    cook_time: Number(cookTime),
                    serves: Number(serves)
                }
            },
            {
                onSuccess: (res) => {
                    toast.success(res.message)
                    queryClient.invalidateQueries(recipesQueryKey())
                    queryClient.invalidateQueries(menusQueryKey())
                    navigate(-1)
                }
            }
        )
    }

    return (
        <div>
            <UrlModal title='Edit Recipe' onClose={() => navigate(-1)}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <ModalBody>
                            <FormRow>
                                <InputField.HookForm label='Name' name='name' />
                            </FormRow>
                            <FormRow>
                                <Label>Image</Label>
                                <div className='flex justify-between items-center w-full'>
                                    <img src={getSingleRecipeData?.image_url || NoImagePlaceholder} className='h-16 w-16 object-cover rounded-md' />
                                    <Link to={`/recipes/edit/${getSingleRecipeData?.id || ''}/upload-image`}>Edit</Link>
                                </div>
                            </FormRow>
                            <FormRow>
                                <Combobox.HookForm
                                    name='recipeCategoryName'
                                    label='Category'
                                    options={(getRecipeCategoriesData || []).map((cat) => ({
                                        id: cat.id,
                                        value: cat.name
                                    }))}
                                    placeholder='Search'
                                    className='!w-full'
                                />
                            </FormRow>
                            <FormRow>
                                <InputField.HookForm label='Prep Time (mins)' name='prepTime' type='number' step={1} />
                            </FormRow>
                            <FormRow>
                                <InputField.HookForm label='Cook Time (mins)' name='cookTime' type='number' step={1} />
                            </FormRow>
                            <FormRow>
                                <InputField.HookForm label='Serves' name='serves' type='number' step={1} />
                            </FormRow>
                            <FormRow>
                                <Label>Ingredients</Label>
                                <div className='flex justify-between items-center w-full'>
                                    <p>{(getSingleRecipeData?.items || []).length}</p>
                                    <Link to={`/recipes/edit/${getSingleRecipeData?.id || ''}/ingredients`}>Edit</Link>
                                </div>
                            </FormRow>
                            <FormRow>
                                <Label>Method</Label>
                                <div className='flex justify-between items-center w-full'>
                                    <p>{(getSingleRecipeData?.method || []).length} steps</p>
                                    <Link to={`/recipes/edit/${getSingleRecipeData?.id || ''}/method`}>Edit</Link>
                                </div>
                            </FormRow>
                        </ModalBody>
                        <ModalFooter
                            buttons={[
                                <Button key={1} color='secondary' onClick={() => navigate(-1)}>
                                    Back
                                </Button>,
                                <SubmitButton key={2} isSubmitting={isSubmitting} isValid={isValid} isDirty={true} text='Save' />
                            ]}
                        />
                    </form>
                </FormProvider>
            </UrlModal>
        </div>
    )
}

export default EditRecipeDetailsForm
